/**
 * Navigation helpers for the OAuth 2.1 grant flow (MCP connector sign-in).
 *
 * The consent screen and the client's redirect URI are reached with FULL
 * browser navigations, never SPA router pushes: after sign-in the session
 * cookie is brand new (a hard load re-runs every guard cleanly), and the
 * post-approve redirect leaves our origin entirely (claude.ai, or a
 * localhost callback for Claude Code). Tests mock this module to observe
 * navigations without fighting jsdom's location.
 */

/** SPA consent screen URL for an in-flight grant (login/signup resume target). */
export function consentPageUrl(consentCode: string, clientId: string): string {
  return `/oauth/consent?consent_code=${encodeURIComponent(consentCode)}&client_id=${encodeURIComponent(clientId)}`;
}

/**
 * Login URL carrying an in-flight grant. Matches the shape better-auth's
 * authorize endpoint uses when it bounces unauthenticated users to the login
 * page (`code` IS the consent_code); login's resume branch reads it back.
 */
export function loginUrlWithGrant(consentCode: string, clientId: string): string {
  return `/login?client_id=${encodeURIComponent(clientId)}&code=${encodeURIComponent(consentCode)}`;
}

/**
 * Replay URL for the login-resume path. The authorize endpoint bounces
 * unauthenticated users to /login?<FULL ORIGINAL AUTHORIZE QUERY>
 * (client_id, redirect_uri, scope, state, code_challenge, …), so after a
 * successful sign-in we replay that query verbatim against authorize — the
 * fresh session cookie lets it proceed (to the consent screen when consent
 * is required). This is the ONLY resume path: better-auth's after-hook is
 * disabled server-side.
 */
export function authorizeReplayUrl(): string {
  return `/api/auth/mcp/authorize${window.location.search}`;
}

/**
 * Append the current URL's query string to an auth-page path (login ↔ signup
 * footer links) so an in-flight OAuth grant survives the swap. Degrades to
 * the bare path when there is no query.
 */
export function withCurrentSearch(path: string): string {
  return `${path}${window.location.search}`;
}

/** Full browser navigation (fresh page load, cookies ride along). */
export function hardNavigate(url: string): void {
  window.location.assign(url);
}

// ---------------------------------------------------------------------------
// Workspace invitations
// ---------------------------------------------------------------------------

/** The SPA accept page for an invitation — the resume target after auth. */
export function acceptInvitationUrl(invitationId: string): string {
  return `/accept-invitation/${encodeURIComponent(invitationId)}`;
}

/**
 * Auth-page URL (login or signup) that remembers the invitation being accepted,
 * so the page can send the invitee back to it once they have a session. Not
 * withCurrentSearch: the accept page carries no query of its own to preserve.
 */
export function authUrlWithInvitation(path: string, invitationId: string): string {
  return `${path}?invitation=${encodeURIComponent(invitationId)}`;
}

// ---------------------------------------------------------------------------
// Social sign-in (Google SSO — plan 2026-07-23-google-sso-social-login)
// ---------------------------------------------------------------------------

/**
 * Search params the auth pages branch on: the two grant-bounce shapes
 * (authorize + consent) plus `error`, which better-auth appends to
 * {@link socialErrorUrl} when a provider consent is cancelled or fails.
 */
export interface AuthPageSearch {
  client_id?: string;
  redirect_uri?: string;
  code?: string;
  error?: string;
  /**
   * Invitation id, set when the accept page sent a logged-out invitee here to
   * authenticate. Signing in/up is a DETOUR in that flow, not the destination —
   * without this the invitation is lost at the auth round-trip and the invitee
   * lands in a workspace of their own instead of the one they were invited to.
   */
  invitation?: string;
}

/**
 * Where the browser lands after a successful social sign-in — the SAME three
 * branches the email forms take (login.tsx / signup.tsx): an authorize bounce
 * replays the original authorize request, a consent bounce returns to the
 * consent screen, anything else goes to `/`. Computed at click time so an
 * in-flight MCP grant survives the provider round-trip (plan D5) — which is
 * also why the fallback can't be dashboard-or-wizard directly: connection
 * state isn't knowable until the provider hands a session back, so `/` makes
 * that call on arrival.
 */
export function socialCallbackUrl(search: AuthPageSearch): string {
  if (search.client_id && search.redirect_uri) return authorizeReplayUrl();
  if (search.code && search.client_id) return consentPageUrl(search.code, search.client_id);
  // Ranked below the grant bounces: an MCP grant is a client waiting on a
  // redirect, while an invitation link is durable and can be resumed later.
  if (search.invitation) return acceptInvitationUrl(search.invitation);
  return '/';
}

/**
 * Where a cancelled/failed provider consent returns: the current page with its
 * query intact, so an in-flight grant is not lost (plan AC7). better-auth
 * appends `?error=<code>[&error_description=]` (errors.mjs).
 */
export function socialErrorUrl(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Map the `error` query param better-auth appends to {@link socialErrorUrl}
 * onto a user-facing message; null when there is nothing to show. Neutral
 * wording — a second provider lands on the same pages (plan AC11).
 */
export function socialErrorMessage(code: string | undefined): string | null {
  if (!code) return null;
  return code === 'access_denied' ? 'Sign-in was cancelled.' : 'Sign-in failed. Please try again.';
}
